import { InvoiceBulkActionType, InvoiceStatusEnum, invoiceV2Apis } from "@payping/api";
import { Archive, RedoOutlined, StopOutlined, UnArchiveOutlined } from "@payping/icons";
import { Checkbox, Modal, Space, Toast, Typography } from "@payping/web-components";
import React from "react";
import { useAsyncFn } from "react-use";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppSelector } from "@/utils/redux";
import { ListCardProps } from "@payping/web-components/components/List/types";

interface Props {
  retry: () => void;
  isArchived?: boolean;
  selectedRowKeys: string[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
  data: ListCardProps["dataSource"];
}

export const ListBulkActions: React.FC<Props> = (props) => {
  const { setSelectedRowKeys, data = [], selectedRowKeys, isArchived, retry } = props;
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const list = useAppSelector((state) => state.invoices.list);
  const allRowsKey = data.map((row) => String(row.key));
  const isAllRowsKeySelected = allRowsKey.length === selectedRowKeys.length;

  const [{ loading: loadingPaidInvoicesCodes }, getPaidInvoicesCodes] = useAsyncFn(
    async (bulkActionType: InvoiceBulkActionType) => {
      switch (bulkActionType) {
        case InvoiceBulkActionType.Reminder:
          return invoiceV2Apis.invoiceCheckBulkReminder({
            bulkActionType,
            codes: selectedRowKeys,
          });
        case InvoiceBulkActionType.Cancellation:
          return invoiceV2Apis.invoiceCheckBulkCancelation({ bulkActionType, codes: selectedRowKeys });
        default:
          return Promise.resolve([""]);
      }
    },
    [selectedRowKeys],
  );

  async function handleBulkAction(finalSelectedRowKeys: string[], bulkActionType: InvoiceBulkActionType) {
    switch (bulkActionType) {
      case InvoiceBulkActionType.Cancellation:
        await invoiceV2Apis.invoiceBulkCancel({ bulkActionType, codes: finalSelectedRowKeys });
        break;
      case InvoiceBulkActionType.Reminder:
        await invoiceV2Apis.invoiceBulkReminder({ bulkActionType, codes: finalSelectedRowKeys });
        break;
      case InvoiceBulkActionType.Archive:
      case InvoiceBulkActionType.DeArchive:
        await invoiceV2Apis.invoiceBulkChangeArchive({ bulkActionType, codes: finalSelectedRowKeys });
        break;
      default:
        break;
    }
    // Clear all selections after operation
    setSelectedRowKeys([]);
    retry();
  }

  async function onBulkActionClick(bulkActionType: InvoiceBulkActionType): Promise<void> {
    let confirmText = t(`invoice.confirm.${InvoiceBulkActionType[bulkActionType]}`);
    // selectedRowKeys can be updated so we need to pass the updated values to callBulkAction
    let finalSelectedRowKeys = selectedRowKeys;
    if (bulkActionType === InvoiceBulkActionType.Cancellation) {
      const paidInvoiceCodes = await getPaidInvoicesCodes(bulkActionType);
      if (paidInvoiceCodes.length) {
        confirmText = t("invoice.confirm.cancelSome", { codes: paidInvoiceCodes.join(", ") });
        const notPaidInvoices = selectedRowKeys.filter(
          (selectedCode) => !paidInvoiceCodes.find((paidInvoiceCode) => paidInvoiceCode === selectedCode),
        );
        finalSelectedRowKeys = notPaidInvoices;
        setSelectedRowKeys(notPaidInvoices);
      }
    } else if (bulkActionType === InvoiceBulkActionType.Reminder) {
      const wrongStatusCodes = list
        .filter(
          (invoice) =>
            selectedRowKeys.includes(invoice.code as string) &&
            invoice.status !== InvoiceStatusEnum.AWAITING_PAYMENT &&
            invoice.status !== InvoiceStatusEnum.DELAYED,
        )
        .map((invoice) => invoice.code);
      if (wrongStatusCodes.length) {
        confirmText = t("invoice.confirm.remindSome", {
          codes: wrongStatusCodes.join(", "),
        });
        const notWrongStatusCodes = selectedRowKeys.filter(
          (selectedCode) => !wrongStatusCodes.find((wrongStatusInvoiceCode) => wrongStatusInvoiceCode === selectedCode),
        );
        finalSelectedRowKeys = notWrongStatusCodes;
        setSelectedRowKeys(notWrongStatusCodes);
      }
    }
    if (finalSelectedRowKeys.length) {
      modal.confirm({
        cancelText: t("cancel"),
        content: confirmText,
        maskClosable: true,
        okText: t("ok"),
        onOk: async () => handleBulkAction(finalSelectedRowKeys, bulkActionType),
      });
    } else {
      const modalContent =
        bulkActionType === InvoiceBulkActionType.Cancellation
          ? t("invoice.confirm.cancelNone")
          : t("invoice.confirm.remindNone");
      modal.error({ content: modalContent });
    }
  }

  return (
    <Toast visible={Boolean(selectedRowKeys.length)} loading={loadingPaidInvoicesCodes}>
      <Space size={"small"} className={"tw-w-full"} direction={"vertical"}>
        <div className={"tw-flex tw-justify-start"}>
          <Toast.Item>
            <Space size={"small"}>
              <Checkbox
                onClick={() => {
                  if (isAllRowsKeySelected) {
                    setSelectedRowKeys([]);
                    return;
                  }
                  setSelectedRowKeys(allRowsKey);
                }}
                checked={isAllRowsKeySelected}
                indeterminate={!isAllRowsKeySelected && selectedRowKeys.length > 0}
              >
                <Typography.Text type={"secondary"}>{t("common.operation.selectAll")}</Typography.Text>
              </Checkbox>
              <Typography.Text type={"secondary"}>{`${selectedRowKeys.length} ${t(
                "common.selectedItem",
              )}`}</Typography.Text>
            </Space>
          </Toast.Item>
        </div>
        <Space>
          {isArchived ? (
            <Toast.Item
              label={t("common.operation.unArchive")}
              Icon={() => <UnArchiveOutlined width={16} />}
              onClick={() => onBulkActionClick(InvoiceBulkActionType.DeArchive)}
            />
          ) : (
            <Toast.Item
              label={t("common.operation.archive")}
              Icon={() => <Archive width={16} />}
              onClick={() => onBulkActionClick(InvoiceBulkActionType.Archive)}
            />
          )}
          <Toast.Item
            label={t("common.operation.cancel")}
            Icon={() => <StopOutlined width={16} />}
            onClick={() => onBulkActionClick(InvoiceBulkActionType.Cancellation)}
          />
          <Toast.Item
            label={t("common.operation.reminder")}
            Icon={() => <RedoOutlined width={16} />}
            onClick={() => onBulkActionClick(InvoiceBulkActionType.Reminder)}
          />
        </Space>
      </Space>
      {contextHolder}
    </Toast>
  );
};
